import { ONE_MINUTE_MS } from '@eflight/shared';
import { useEffect, useState } from 'react';

/** Folder of the weather product's per-letter CSV files on S3. */
export type S3WeatherProduct = 'metar' | 'taf';

export const useS3Taf = (icao: string) => useS3Weather('taf', icao);
export const useS3Metar = (icao: string) => useS3Weather('metar', icao);

/** undefined while loading, null when the airport has no report. */
function useS3Weather(product: S3WeatherProduct, icao: string) {
    const [report, setReport] = useState<string | null | undefined>(undefined);
    useEffect(() => {
        let cancelled = false;
        setReport(undefined);
        (async () => {
            const data = await getS3Weather(product, icao);
            if (cancelled) {
                return;
            }
            setReport(data);
        })();

        return () => {
            cancelled = true;
        };
    }, [product, icao]);

    return report;
}

type S3WeatherCacheObject = {
    timeRequested: Date;
    reports: Promise<Map<string, string>>;
};
/** `${product}/${firstLetter}` → download */
const cache = new Map<string, S3WeatherCacheObject>();

export const getS3Taf = async (icao: string) => getS3Weather('taf', icao);
export const getS3Metar = async (icao: string) => getS3Weather('metar', icao);

async function getS3Weather(product: S3WeatherProduct, icao: string): Promise<string | null> {
    icao = icao.toUpperCase();
    const reports = await maybeDownloadLetter(product, icao[0]);

    return reports.get(icao) ?? null;
}

async function maybeDownloadLetter(product: S3WeatherProduct, firstLetter: string): Promise<Map<string, string>> {
    const key = `${product}/${firstLetter}`;
    const cacheObject = cache.get(key);
    if (cacheObject && cacheObject.timeRequested.getTime() > Date.now() - 2 * ONE_MINUTE_MS) {
        return cacheObject.reports;
    }
    const newReports = downloadLetter(product, firstLetter);
    cache.set(key, {
        timeRequested: new Date(),
        reports: newReports,
    });

    return newReports;
}

async function downloadLetter(product: S3WeatherProduct, firstLetter: string): Promise<Map<string, string>> {
    try {
        return (await fetchWeatherLetter(product, firstLetter)).reports;
    } catch (error) {
        console.error(`Error downloading ${product.toUpperCase()}s for letter ${firstLetter}`, error);

        return new Map();
    }
}

export type WeatherLetterDownload = {
    /** ICAO → raw METAR/TAF text. */
    reports: Map<string, string>;
    /** When the file was last updated on S3; null if the response does not say. */
    lastModified: Date | null;
};

/** Downloads the METARs/TAFs of all airports whose ICAO starts with `firstLetter`. */
export async function fetchWeatherLetter(
    product: S3WeatherProduct,
    firstLetter: string
): Promise<WeatherLetterDownload> {
    const url = `https://alol-taf.s3.eu-central-1.amazonaws.com/${product}/${firstLetter}.csv`;
    // S3 sends no Cache-Control, so without revalidation the browser may heuristically serve a stale copy
    const response = await fetch(url, { cache: 'no-cache' });
    if (!response.ok) {
        throw new Error(`${product.toUpperCase()} download for ${firstLetter} failed: HTTP ${response.status}`);
    }
    const lastModifiedHeader = response.headers.get('Last-Modified');
    const lastModified = lastModifiedHeader ? new Date(lastModifiedHeader) : null;

    return {
        reports: parseTafLetterCsv(await response.text()),
        lastModified: lastModified && !Number.isNaN(lastModified.getTime()) ? lastModified : null,
    };
}

export function parseTafLetterCsv(data: string): Map<string, string> {
    const out = new Map<string, string>();
    for (const line of data.split('\n')) {
        const [icao, taf] = line.split(/,(.*)/s); // only the first comma separates the ICAO code
        if (icao && taf) {
            out.set(icao, taf);
        }
    }

    return out;
}
